import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, map, switchMap, takeWhile } from 'rxjs/operators';

import { AppUrlConstants } from '../../../core/constants/AppUrlConstants';
import {
  WebsiteCrawlCreateResponse,
  WebsiteCrawlDocumentsResponse,
  WebsiteCrawlErrorResponse,
  WebsiteCrawlHistoryResponse,
  WebsiteCrawlReport,
  WebsiteCrawlRequest,
  WebsiteCrawlStatus,
  WebsiteCrawlStatusResponse,
  WebsiteKnowledgeImportResponse,
  WebsiteCrawlQaListResponse,
  isTerminalStatus
} from './website-crawl.models';

/** Poll interval for in-flight crawls, in milliseconds. */
export const CRAWL_POLL_INTERVAL_MS = 3000;

/**
 * Talks to the GRMS Spring crawler API only. The Python crawler on
 * 127.0.0.1:8000 is never contacted from the browser: Spring proxies it, which
 * is what keeps authentication enforced and crawler host paths hidden.
 *
 * Requests go through the standard AppUrlConstants host, so the existing auth
 * and spinner interceptors apply unchanged.
 */
@Injectable({ providedIn: 'root' })
export class WebsiteCrawlService {

  constructor(private http: HttpClient) {}

  /** POST /api/v1/chat/website-crawls */
  startCrawl(request: WebsiteCrawlRequest): Observable<WebsiteCrawlCreateResponse> {
    return this.http
      .post<WebsiteCrawlCreateResponse>(AppUrlConstants.WEBSITE_CRAWLS, request)
      .pipe(
        map(response => ({ ...response, status: normalizeStatus(response.status) })),
        catchError(error => throwError(() => toSafeMessage(error)))
      );
  }

  recrawl(crawlId: string): Observable<WebsiteCrawlCreateResponse> {
    return this.http
      .post<WebsiteCrawlCreateResponse>(
        `${AppUrlConstants.WEBSITE_CRAWLS}/${encodeURIComponent(crawlId)}/recrawl`, {})
      .pipe(
        map(response => ({ ...response, status: normalizeStatus(response.status) })),
        catchError(error => throwError(() => toSafeMessage(error)))
      );
  }

  deleteCrawl(crawlId: string): Observable<void> {
    return this.http
      .delete<void>(`${AppUrlConstants.WEBSITE_CRAWLS}/${encodeURIComponent(crawlId)}`)
      .pipe(catchError(error => throwError(() => toSafeMessage(error))));
  }

  /** GET /api/v1/chat/website-crawls/{crawlId} */
  getCrawlStatus(crawlId: string): Observable<WebsiteCrawlStatusResponse> {
    return this.http
      .get<WebsiteCrawlStatusResponse>(`${AppUrlConstants.WEBSITE_CRAWLS}/${encodeURIComponent(crawlId)}`)
      .pipe(
        map(response => ({ ...response, status: normalizeStatus(response.status) })),
        catchError(error => throwError(() => toSafeMessage(error)))
      );
  }

  /** GET /api/v1/chat/website-crawls */
  getCrawlHistory(limit = 50, offset = 0): Observable<WebsiteCrawlHistoryResponse> {
    return this.http
      .get<WebsiteCrawlHistoryResponse>(AppUrlConstants.WEBSITE_CRAWLS, {
        params: { limit, offset, sort: 'created_at_desc' }
      })
      .pipe(
        map(response => ({
          ...response,
          items: response.items.map(item => ({ ...item, status: normalizeStatus(item.status) }))
        })),
        catchError(error => throwError(() => toSafeMessage(error)))
      );
  }

  getCrawlDocuments(crawlId: string, limit = 50, offset = 0): Observable<WebsiteCrawlDocumentsResponse> {
    return this.http
      .get<WebsiteCrawlDocumentsResponse>(
        `${AppUrlConstants.WEBSITE_CRAWLS}/${encodeURIComponent(crawlId)}/documents`,
        { params: { limit, offset } }
      )
      .pipe(catchError(error => throwError(() => toSafeMessage(error))));
  }

  getCrawlReport(crawlId: string): Observable<WebsiteCrawlReport> {
    return this.http
      .get<WebsiteCrawlReport>(`${AppUrlConstants.WEBSITE_CRAWLS}/${encodeURIComponent(crawlId)}/report`)
      .pipe(catchError(error => throwError(() => toSafeMessage(error))));
  }

  createQaList(crawlId: string): Observable<WebsiteCrawlQaListResponse> {
    return this.http
      .post<WebsiteCrawlQaListResponse>(
        `${AppUrlConstants.WEBSITE_CRAWLS}/${encodeURIComponent(crawlId)}/qa-list`, {})
      .pipe(catchError(error => throwError(() => toSafeMessage(error))));
  }

  downloadQaList(crawlId: string): Observable<Blob> {
    return this.http
      .get(`${AppUrlConstants.WEBSITE_CRAWLS}/${encodeURIComponent(crawlId)}/qa-list.csv`, {
        responseType: 'blob'
      })
      .pipe(catchError(error => throwError(() => toSafeMessage(error))));
  }

  /** POST /api/v1/chat/website-knowledge/crawls/{crawlId}/import */
  importCompletedCrawl(
    crawlId: string,
    sourceName = 'Website Knowledge Crawler',
    replaceExisting = true,
    language = 'auto'
  ): Observable<WebsiteKnowledgeImportResponse> {
    return this.http
      .post<WebsiteKnowledgeImportResponse>(
        `${AppUrlConstants.WEBSITE_KNOWLEDGE_CRAWL_IMPORT}/${encodeURIComponent(crawlId)}/import`,
        {},
        { params: { sourceName, replaceExisting, language } }
      )
      .pipe(catchError(error => throwError(() => toSafeMessage(error))));
  }

  /**
   * Emits the crawl status every {@link CRAWL_POLL_INTERVAL_MS} until a terminal
   * status is reached, then completes. The terminal value is emitted before
   * completing so the caller sees the final report.
   *
   * The stream completes on its own, but callers must still unsubscribe on
   * destroy to stop a crawl that is still running.
   */
  pollCrawlStatus(crawlId: string): Observable<WebsiteCrawlStatusResponse> {
    return timer(0, CRAWL_POLL_INTERVAL_MS).pipe(
      switchMap(() => this.getCrawlStatus(crawlId)),
      takeWhile(status => !isTerminalStatus(status.status), true)
    );
  }
}

/**
 * Guards against casing drift between the crawler, Spring and the UI. Spring
 * already upper-cases the status, so this is a safety net rather than the
 * primary normalization point: an unrecognised value is surfaced as FAILED
 * instead of silently keeping the crawl polling forever.
 */
export function normalizeStatus(status: string | null | undefined): WebsiteCrawlStatus {
  switch ((status ?? '').trim().toUpperCase()) {
    case 'QUEUED':
      return 'QUEUED';
    case 'RUNNING':
      return 'RUNNING';
    case 'COMPLETED':
      return 'COMPLETED';
    case 'INTERRUPTED':
      return 'INTERRUPTED';
    default:
      return 'FAILED';
  }
}

/**
 * Converts an HTTP failure into a message that is safe to show. Only the
 * structured body produced by WebsiteCrawlExceptionHandler is trusted; anything
 * else (HTML error pages, Java or Python stack traces, connection details) is
 * replaced by a generic message.
 */
export function toSafeMessage(error: unknown): string {
  if (!(error instanceof HttpErrorResponse)) {
    return 'Something went wrong while contacting the crawler service.';
  }

  const body = error.error as Partial<WebsiteCrawlErrorResponse> | null;
  if (body && typeof body.code === 'string' && typeof body.message === 'string') {
    return body.message;
  }

  switch (error.status) {
    case 0:
      return 'The crawler service is currently unavailable.';
    case 401:
    case 403:
      return 'You are not authorised to manage website crawls.';
    case 404:
      return 'This crawl job could not be found.';
    case 409:
      return 'The extracted crawl documents are not available yet. Please wait a moment and try training again.';
    case 503:
      return 'The crawler service is currently unavailable.';
    case 504:
      return 'The crawler service did not respond in time.';
    default:
      return 'Something went wrong while contacting the crawler service.';
  }
}
